import React, { useContext, useEffect, useState } from "react";
import { Box, DialogProps } from "@material-ui/core";
import Grid from "@material-ui/core/Grid/Grid";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import CustomTextField from "../../../components/Input/CustomTextField";
import CustomDropdown from "../../../components/Input/CustomDropdown";
import CustomButton from "../../../components/Button/CustomButton";
import CustomTreeView, { Tree } from "./CustomTreeView";
import { tesaGrey4 } from "../../../styles/colors";
import SubstrateClass from "../../../api/models/substrate/SubstrateClass";
import TesaTmbApi, { mapSubstrateClass, mapSubstrateClassTree } from "../../../api/services/TesaTmbApi";
import { SubstrateSampleContext } from "../../../contexts/SubstrateSampleContext";
import CustomLoading from "../../../components/Alert/CustomLoading";
import CustomAlert from "../../../components/Alert/CustomAlert";
import {
  keyOfSubstrateClassSelection,
  labelOfSubstrateClassSelection,
} from "../../../api/models/substrate/SubstrateClassSelection";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import { ProductSampleAddFormType } from "../../../api/models/product/ProductSampleAddFormType";
import SubstrateClassTapeSelection from "../../../api/models/substrate/SubstrateClassTapeSelection";
import { labelOf, TapeSampleProductType } from "../../../api/models/substrate/TapeSampleProductType";
import PrototypeSampleAddForm from "../ProductTab/PrototypeSampleAddForm";
import PrototypeSampleAddFormType from "../../../api/models/product/PrototypeSampleAddFormType";
import { TesaPrototype } from "../../../api/models/product/TesaPrototype";
import CompetitorSampleAddForm from "../ProductTab/CompetitorSampleAddForm";
import UnknownSampleAddForm from "../ProductTab/UnknownSampleAddForm";
import UnknownSampleAddFormType, { UnknownProduct } from "../../../api/models/product/UnknownSampleAddFormType";
import ProductSampleAddForm from "../ProductTab/ProductSampleAddForm";
import SampleSuggestionField from "../SampleSuggestionField";
import InfoBox from "../../../components/InfoBox";
import VerticalAlignedChip from "../../../components/Chip/VerticalAlignedChip";
import { ProductSuggestionInnerFormikProps } from "../DescriptionTab/models/ProductSuggestionForm/SuggestionForm";
import { CompetitorSampleAddFormType } from "../DescriptionTab/models/CompetitorSuggestionForm/CompetitorSuggestionForm";

const INFO_TEXT = `
  If a substrate consists of several layers, e.g. coated or metalized material, 
  the substrate should be designed as a multilayer substrate. You do this by selecting several 
  materials. The first selection is the top layer the tape will be applied on.
`;

const HELPER_TEXT = `
  * Describe the material of the substrate sample – you can choose from a tree. 
  * Be as specific as you can.  
  * If you are unable to choose between the different child nodes choose the parent node.
`;

const useStyles = makeStyles({
  inputHeadline: {
    color: "#757575",
    fontSize: "0.75rem",
    lineHeight: "1.5rem",
  },
});

interface SubstrateAddDialogProps extends DialogProps {
  onClose: () => void;
  onSave: () => void;
  preSelectedProductType: string | null;
}

const SubstrateAddDialog = (props: SubstrateAddDialogProps): JSX.Element => {
  const { open, onClose, onSave, preSelectedProductType } = props;
  const dialogContext = useContext(ConfirmationDialogContext);
  const tesaTmbApi: TesaTmbApi = new TesaTmbApi(dialogContext);
  const classes = useStyles();
  const {
    testRequest,
    substrateContext,
    substrateClasses,
    updateSubstrateClasses,
    updateSubstrateObject,
    updateSubstrateId,
  } = useContext(SubstrateSampleContext);
  const [treeDropdown, setTreeDropdown] = useState<SubstrateClass[] | null>(null);
  const [tree, setTree] = useState<Tree<SubstrateClass>[] | null>(null);
  const [selectedTree, setSelectedTree] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [secondaryDropdown, setSecondaryDropdown] = useState({
    values: [] as string[],
    selected: "",
    label: "",
  });
  const [tapeSampleProductType, setTapeSampleProductType] = useState<TapeSampleProductType | "">("");

  const [initialProductType, setInitialProductType] = useState<string | null>(preSelectedProductType);

  const [selectedTreeClass, setSelectedTreeClass] = useState<SubstrateClass | null>(null);

  const [selectedTape, setSelectedTape] = useState<
    | ProductSampleAddFormType
    | TesaPrototype
    | CompetitorSampleAddFormType
    | UnknownProduct
    | ProductSuggestionInnerFormikProps
    | null
  >(null);

  const tapeSubstrateClass = treeDropdown?.find((treeOption) => treeOption.name === "Tape");
  const isTapeSample = selectedTree === tapeSubstrateClass?.tmbId;

  const tapeFormHandlers = {
    "tesa Product": (formData: ProductSuggestionInnerFormikProps) => {
      setSelectedTape(formData.nartField.nart ? formData : null);
    },
    "tesa Prototype": (formData: PrototypeSampleAddFormType | null) => {
      setSelectedTape(formData?.prototype || null);
    },
    "Competitor Product": (formData: CompetitorSampleAddFormType | null) => {
      setSelectedTape(formData || null);
    },
    "Unknown Product": (formData: UnknownSampleAddFormType) => {
      setSelectedTape(formData.unknownProduct);
    },
  };

  const renderTapeForm = (tapeType: TapeSampleProductType | ""): JSX.Element | null => {
    switch (tapeType) {
      case "tesa Product":
        return <ProductSampleAddForm handleFormChange={tapeFormHandlers[tapeType]} hideSubmit oneRow />;
      case "tesa Prototype":
        return <PrototypeSampleAddForm halfWidth handleFormChange={tapeFormHandlers[tapeType]} hideSubmit />;
      case "Competitor Product":
        return <CompetitorSampleAddForm handleFormChange={tapeFormHandlers[tapeType]} hideSubmit />;
      case "Unknown Product":
        return <UnknownSampleAddForm halfWidth handleFormChange={tapeFormHandlers[tapeType]} hideSubmit />;
      default:
        return null;
    }
  };

  const generateTapeSampleName = (
    productType: TapeSampleProductType,
    productData:
      | ProductSampleAddFormType
      | TesaPrototype
      | CompetitorSampleAddFormType
      | UnknownProduct
      | ProductSuggestionInnerFormikProps,
  ): string => {
    switch (productType) {
      case "tesa Product":
        return (
          (productData as ProductSampleAddFormType).productNart?.id ||
          (productData as ProductSuggestionInnerFormikProps).nartField?.nart ||
          ""
        );
      case "tesa Prototype": {
        const prototype = productData as TesaPrototype;
        return prototype?.name && prototype?.responsibleLaboratory?.name
          ? `${prototype.name} (${prototype.responsibleLaboratory.name})`
          : "";
      }
      case "Competitor Product": {
        const competitor = (productData as CompetitorSampleAddFormType).product;
        return competitor?.name || "";
      }
      case "Unknown Product":
        return (productData as UnknownProduct).name || "";
      default:
        return "";
    }
  };

  const generateProductId = (
    productType: TapeSampleProductType,
    productData:
      | ProductSampleAddFormType
      | TesaPrototype
      | CompetitorSampleAddFormType
      | UnknownProduct
      | ProductSuggestionInnerFormikProps,
  ) => {
    switch (productType) {
      case "tesa Product":
        return (
          (productData as ProductSampleAddFormType).productNart?.id ||
          (productData as ProductSuggestionInnerFormikProps).nartField?.nart ||
          ""
        );
      case "tesa Prototype":
        return (productData as TesaPrototype).id?.toString() || "";
      case "Competitor Product":
        return (productData as CompetitorSampleAddFormType).product?.id?.toString() || "";
      case "Unknown Product":
        return (productData as UnknownProduct).id?.toString() || "";
      default:
        return "";
    }
  };

  const toSubstrateClassTapeSelection = (
    productData:
      | ProductSampleAddFormType
      | TesaPrototype
      | CompetitorSampleAddFormType
      | UnknownProduct
      | ProductSuggestionInnerFormikProps
      | null,
  ): SubstrateClassTapeSelection | undefined => {
    if (!productData || !tapeSubstrateClass) {
      return;
    }
    const productType = tapeSampleProductType as TapeSampleProductType;
    const productId = generateProductId(productType, productData);
    const productName = generateTapeSampleName(productType, productData);

    return {
      kind: "tape",
      substrateClass: {
        ...tapeSubstrateClass,
        productId,
        productName,
      },
      substrateClassType: productType,
    };
  };

  useEffect(() => {
    if (open) {
      tesaTmbApi
        .fetchSubstrateRoots()
        .then((response) => {
          const rootSubstrateClasses = response.data.sort((a, b) => a.id - b.id).map((t) => mapSubstrateClass(t));
          setTreeDropdown(rootSubstrateClasses);
          const tapeClass = rootSubstrateClasses.find((rootSubstrateClass) => rootSubstrateClass.name === "Tape");
          if (initialProductType && tapeClass) {
            setSelectedTree(tapeClass.tmbId);
          } else {
            setSelectedTree(rootSubstrateClasses[0].tmbId);
          }
        })
        .catch(() => {
          onClose();
        });
    } else {
      setSearchTerm("");
      setSelectedTree(null);
      setTreeDropdown(null);
      setSelectedTreeClass(null);
      setTree(null);
      setSecondaryDropdown({
        values: [],
        selected: "",
        label: "Type",
      });
      setSelectedTape(null);
      setTapeSampleProductType("");
    }
  }, [open]);

  useEffect(() => {
    if (!selectedTree) {
      return;
    }
    setSelectedTreeClass(null);
    setTree(null);
    setSecondaryDropdown({
      values: [],
      selected: "",
      label: "Type",
    });
    setTapeSampleProductType("");
    tesaTmbApi
      .fetchSubstrateTree(selectedTree, substrateContext)
      .then((response) => {
        const mappedTree = mapSubstrateClassTree(response.data);
        setTree(mappedTree.children);
        setTapeSampleProductType("");
        const values = mappedTree.data.types;
        values.sort((a, b) => {
          if (a.startsWith("tesa") && !b.startsWith("tesa")) {
            return -1;
          }
          if (!a.startsWith("tesa") && b.startsWith("tesa")) {
            return 1;
          }
          return a.localeCompare(b);
        });
        setSecondaryDropdown({
          values,
          selected: labelOf(initialProductType),
          label: "Type",
        });
        setTapeSampleProductType(labelOf(initialProductType) as TapeSampleProductType);
        setInitialProductType(null);
        setSelectedTreeClass(null);
        setSelectedTape(null);
      })
      .catch(() => {
        setTree([]);
        setTapeSampleProductType("");
      });
  }, [selectedTree]);

  const tapeSelection = toSubstrateClassTapeSelection(selectedTape);

  const renderContent = (): JSX.Element => {
    if (!treeDropdown) {
      return <CustomLoading />;
    }
    return (
      <Grid container justify="flex-start" direction="row" spacing={4}>
        <Grid item xs={12}>
          <CustomAlert text={INFO_TEXT} severity="info" />
        </Grid>
        <Grid item xs={12}>
          <SampleSuggestionField sampleTypeName="Substrate" value={testRequest?.description?.substrates || null} />
        </Grid>
        <Grid item xs={6}>
          <CustomDropdown
            disabled={tree === null}
            labelText="Category"
            value={selectedTree}
            onChange={(event): void => {
              setSelectedTree(event.target.value as number);
            }}
          >
            {treeDropdown.map((treeValue) => {
              return (
                <MenuItem key={treeValue.tmbId} value={treeValue.tmbId}>
                  {treeValue.name}
                </MenuItem>
              );
            })}
          </CustomDropdown>
        </Grid>
        <Grid item xs={6}>
          {!isTapeSample && (
            <CustomTextField
              fullWidth
              disabled={tree === null}
              label="Search Substrate"
              value={searchTerm}
              onChange={(event): void => {
                const term = event.target.value;
                setSearchTerm(term);
              }}
              endIcon={<SearchIcon fontSize="default" />}
            />
          )}
        </Grid>
        {secondaryDropdown.values.length > 0 && (
          <Grid item xs={6}>
            <CustomDropdown
              disabled={tree === null}
              labelText={secondaryDropdown.label}
              value={secondaryDropdown.selected}
              onChange={(event): void => {
                const selected = event.target.value as string;
                setSecondaryDropdown({ ...secondaryDropdown, selected });
                if (isTapeSample) {
                  setTapeSampleProductType(selected as TapeSampleProductType);
                  setSelectedTape(null);
                }
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {secondaryDropdown.values.map((value) => {
                return (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                );
              })}
            </CustomDropdown>
          </Grid>
        )}
        {(secondaryDropdown.values.length === 0 || (secondaryDropdown.selected && !isTapeSample)) && (
          <Grid item xs={12}>
            <Box border={`1px solid ${tesaGrey4}`} style={{ maxHeight: 300, overflowX: "hidden", overflowY: "auto" }}>
              <Box padding={2}>
                {tree ? (
                  <CustomTreeView<SubstrateClass>
                    highlighted={searchTerm}
                    onNodeSelect={setSelectedTreeClass}
                    nodes={tree}
                  />
                ) : (
                  <CustomLoading />
                )}
              </Box>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box>{renderTapeForm(tapeSampleProductType)}</Box>
        </Grid>
        {substrateClasses.length === 0 ? null : (
          <Grid item xs={12}>
            <Box className={classes.inputHeadline} marginTop={-1} paddingBottom={0} paddingLeft={1}>
              Surface
            </Box>
            <Box borderBottom={`1px solid ${tesaGrey4}`} paddingBottom={1}>
              {substrateClasses.map((selection, index) => (
                <Box
                  component="span"
                  display="inline-block"
                  paddingRight={1}
                  paddingBottom={1}
                  key={keyOfSubstrateClassSelection(selection, index)}
                >
                  <VerticalAlignedChip
                    label={labelOfSubstrateClassSelection(selection)}
                    color={index === 0 ? "primary" : undefined}
                    variant={index === 0 ? undefined : "outlined"}
                    onDelete={(): void => {
                      updateSubstrateClasses((prev) => {
                        const deleteIndex = prev.findIndex((c, selectionIndex) => selectionIndex === index);
                        if (deleteIndex >= 0) {
                          const copy = [...prev];
                          copy.splice(deleteIndex, 1);
                          return copy;
                        }
                        return prev;
                      });
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Grid>
        )}

        <Grid container item xs={12}>
          <InfoBox
            flipChildren
            compress
            hidden={substrateClasses.length !== 0}
            text="“Add Surface” means that this is the surface that you want to apply the tape on."
          >
            <Box display="flex" justifyContent="flex-end">
              <CustomButton
                disabled={!(selectedTreeClass || tapeSelection)}
                text={substrateClasses.length === 0 ? "Add Surface" : "Add Layer"}
                onClick={(): void => {
                  if (tapeSelection) {
                    updateSubstrateClasses((prev) => [...prev, tapeSelection]);
                  }
                  if (selectedTreeClass) {
                    updateSubstrateClasses((prev) => [
                      ...prev,
                      {
                        kind: "standard",
                        substrateClass: selectedTreeClass,
                        substrateClassType: secondaryDropdown.selected || null,
                      },
                    ]);
                  }
                }}
              />
              <Box marginLeft={3}>
                <CustomButton
                  disabled={substrateClasses.length === 0}
                  primary
                  text="Create Substrate Sample"
                  onClick={(): void => {
                    updateSubstrateObject(null);
                    updateSubstrateId(null);
                    onSave();
                  }}
                />
              </Box>
            </Box>
          </InfoBox>
        </Grid>
      </Grid>
    );
  };

  return (
    <CustomDialog fullWidth maxWidth="md" onClose={onClose} open={open}>
      <InfoBox compress text={HELPER_TEXT}>
        <h2 style={{ marginBottom: "0px" }}>ADD NEW SUBSTRATE SAMPLE</h2>
      </InfoBox>
      <Box marginBottom="1rem" />
      {renderContent()}
    </CustomDialog>
  );
};

export default SubstrateAddDialog;
